import { z } from "zod"
import { EventKind } from "./event-kind"
import { LayerEntityType, Event } from "./event-registry"

export enum NotificationCategory {
    AccessRequests = "access_requests",
    ProcessingOrtho = "processing.ortho",
    ProcessingHotspot = "processing.hotspot",
    ProcessingColorfinder = "processing.colorfinder",
    ProcessingSatellite = "processing.satellite",
    StreamStarted = "stream.started",
    LocationSharing = "location_sharing",
    Shares = "shares",
    Comments = "comments",
}

export enum NotificationChannel {
    NovaWebApp = "nova_web_app",
    Email = "email",
    Atlas = "atlas",
}

// TODO: @jarren @zach Move this out of schemas and into the notifications package so we can leverage NotifiableEventKind for better type safety
export const eventKindToCategory: Record<EventKind, NotificationCategory | null> = {
    [EventKind.AccessRequested]: NotificationCategory.AccessRequests,
    [EventKind.AccessGranted]: null,
    [EventKind.StreamStarted]: NotificationCategory.StreamStarted,
    [EventKind.LocationSharingStarted]: NotificationCategory.LocationSharing,
    [EventKind.LocationSharingEnded]: NotificationCategory.LocationSharing,
    [EventKind.StreamShared]: NotificationCategory.Shares,
    [EventKind.MissionGroupStreamShared]: NotificationCategory.Shares,
    [EventKind.MissionGroupDashboardShared]: NotificationCategory.Shares,
    [EventKind.LayerCreated]: null,
    [EventKind.LayerDeleted]: null,
    [EventKind.LayerProcessed]: null,
    [EventKind.LayerProcessingFailed]: null,
    [EventKind.LayerArchived]: null,
    [EventKind.LayerUnarchived]: null,
    [EventKind.EntityCreated]: null,
    [EventKind.EntityDeleted]: null,
    [EventKind.EntityArchived]: null,
    [EventKind.EntityUnarchived]: null,
    [EventKind.StreamEnded]: null,
    [EventKind.PdfGenerated]: null,
    [EventKind.PdfFailed]: null,
    [EventKind.AccessRemoved]: null,
    [EventKind.CommentCreated]: NotificationCategory.Comments,
    [EventKind.CommentDeleted]: null,
}

const processingCategoryByLayerType: Partial<Record<LayerEntityType, NotificationCategory>> = {
    orthoFlights: NotificationCategory.ProcessingOrtho,
    infraredFlights: NotificationCategory.ProcessingHotspot,
    videoColorFinderFlights: NotificationCategory.ProcessingColorfinder,
    imageColorFinderFlights: NotificationCategory.ProcessingColorfinder,
    satelliteFlights: NotificationCategory.ProcessingSatellite,
}

export const resolveNotificationCategory = (event: Event): NotificationCategory | null => {
    if (
        event.eventKind === EventKind.LayerProcessed ||
        event.eventKind === EventKind.LayerProcessingFailed
    ) {
        return processingCategoryByLayerType[event.data.layerType] ?? null
    }
    return eventKindToCategory[event.eventKind]
}

export type NotificationCategoryMeta = {
    category: NotificationCategory
    channels: NotificationChannel[]
    defaultEnabled: Record<NotificationChannel, boolean>
}

const allChannels = [
    NotificationChannel.NovaWebApp,
    NotificationChannel.Email,
    NotificationChannel.Atlas,
]

export const notificationCategoryMeta: NotificationCategoryMeta[] = [
    {
        category: NotificationCategory.AccessRequests,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: true,
            [NotificationChannel.Email]: true,
            [NotificationChannel.Atlas]: true,
        },
    },
    {
        category: NotificationCategory.ProcessingOrtho,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: true,
            [NotificationChannel.Email]: false,
            [NotificationChannel.Atlas]: false,
        },
    },
    {
        category: NotificationCategory.ProcessingHotspot,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: true,
            [NotificationChannel.Email]: false,
            [NotificationChannel.Atlas]: false,
        },
    },
    {
        category: NotificationCategory.ProcessingColorfinder,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: true,
            [NotificationChannel.Email]: false,
            [NotificationChannel.Atlas]: false,
        },
    },
    {
        category: NotificationCategory.ProcessingSatellite,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: true,
            [NotificationChannel.Email]: false,
            [NotificationChannel.Atlas]: false,
        },
    },
    {
        category: NotificationCategory.StreamStarted,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: false,
            [NotificationChannel.Email]: false,
            [NotificationChannel.Atlas]: false,
        },
    },
    {
        category: NotificationCategory.Comments,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: true,
            [NotificationChannel.Email]: true,
            [NotificationChannel.Atlas]: true,
        },
    },
    {
        category: NotificationCategory.LocationSharing,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: false,
            [NotificationChannel.Email]: false,
            [NotificationChannel.Atlas]: false,
        },
    },
    {
        category: NotificationCategory.Shares,
        channels: allChannels,
        defaultEnabled: {
            [NotificationChannel.NovaWebApp]: true,
            [NotificationChannel.Email]: true,
            [NotificationChannel.Atlas]: true,
        },
    },
]

export enum SilenceDuration {
    None = "none",
    FifteenMinutes = "15m",
    OneHour = "1h",
    TwoHours = "2h",
    FourHours = "4h",
    EightHours = "8h",
    UntilTomorrow = "until_tomorrow",
}

export const channelPreferencesSchema = z.partialRecord(z.enum(NotificationCategory), z.boolean())

export type ChannelPreferences = z.infer<typeof channelPreferencesSchema>

export const notificationPreferencesBlobSchema = z.partialRecord(
    z.enum(NotificationChannel),
    channelPreferencesSchema
)

export type NotificationPreferencesBlob = z.infer<typeof notificationPreferencesBlobSchema>

export const notificationPreferenceRecordSchema = z.object({
    id: z.uuid(),
    org_id: z.uuid(),
    user_id: z.guid(),
    preferences: notificationPreferencesBlobSchema,
    silence_until: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
})

export const notificationPreferenceSchema = notificationPreferenceRecordSchema.transform((r) => ({
    id: r.id,
    orgId: r.org_id,
    userId: r.user_id,
    preferences: r.preferences,
    silenceUntil: r.silence_until,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
}))

export type NotificationPreferenceRecord = z.infer<typeof notificationPreferenceRecordSchema>
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>

export const notificationPreferenceEntrySchema = z.object({
    category: z.enum(NotificationCategory),
    channel: z.enum(NotificationChannel),
    enabled: z.boolean(),
})

export type NotificationPreferenceEntry = z.infer<typeof notificationPreferenceEntrySchema>

export const notificationPreferencesSchema = z.object({
    silenceUntil: z.string().nullable(),
    entries: z.array(notificationPreferenceEntrySchema),
})

export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>

export const silenceDurationToTimestamp = (
    silence: SilenceDuration,
    now: Date,
    timezoneOffsetMinutes = 0
): string | null => {
    switch (silence) {
        case SilenceDuration.None:
            return null
        case SilenceDuration.FifteenMinutes:
            return new Date(now.getTime() + 15 * 60 * 1000).toISOString()
        case SilenceDuration.OneHour:
            return new Date(now.getTime() + 60 * 60 * 1000).toISOString()
        case SilenceDuration.TwoHours:
            return new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString()
        case SilenceDuration.FourHours:
            return new Date(now.getTime() + 4 * 60 * 60 * 1000).toISOString()
        case SilenceDuration.EightHours:
            return new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString()
        case SilenceDuration.UntilTomorrow: {
            const offsetMs = timezoneOffsetMinutes * 60 * 1000
            const localNow = new Date(now.getTime() - offsetMs)
            const localMidnightTomorrow = Date.UTC(
                localNow.getUTCFullYear(),
                localNow.getUTCMonth(),
                localNow.getUTCDate() + 1
            )
            return new Date(localMidnightTomorrow + offsetMs).toISOString()
        }
    }
}

export const isSilenceActive = (silenceUntil: string | null | undefined, now: Date) =>
    !!silenceUntil && now.getTime() < new Date(silenceUntil).getTime()

export const silenceUntilToDuration = (
    silenceUntil: string | null | undefined,
    now: Date,
    timezoneOffsetMinutes = 0
): SilenceDuration => {
    if (!silenceUntil || !isSilenceActive(silenceUntil, now)) return SilenceDuration.None

    const target = new Date(silenceUntil).getTime()
    const candidates = [
        SilenceDuration.FifteenMinutes,
        SilenceDuration.OneHour,
        SilenceDuration.TwoHours,
        SilenceDuration.FourHours,
        SilenceDuration.EightHours,
        SilenceDuration.UntilTomorrow,
    ]

    let closest = SilenceDuration.OneHour
    let smallestDelta = Infinity
    for (const candidate of candidates) {
        const timestamp = silenceDurationToTimestamp(candidate, now, timezoneOffsetMinutes)
        if (!timestamp) continue
        const delta = Math.abs(new Date(timestamp).getTime() - target)
        if (delta < smallestDelta) {
            smallestDelta = delta
            closest = candidate
        }
    }
    return closest
}
